import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { usePopup } from '../../contexts/PopupContext';
import { supabase } from '../../lib/supabase';
import { getFriends } from '../../utils/friendsService';
import {
  applyDiscoverEventFilters,
  defaultMapDiscoverFilters,
  mapDiscoverFiltersActive,
} from '../../utils/discoverEventFilters';
import { buildMapActiveFilterChips } from '../../utils/mapActiveFilterChips';
import { formatLocalYmd, isEventNotFinishedYet } from '../../utils/eventSchedule';
import SearchPanel from './map/SearchPanel';
import MapFiltersPanel from './map/MapFiltersPanel';
import DiscoverDrawerMapBackdrop from './DiscoverDrawerMapBackdrop';
import LimitWarning from './map/LimitWarning';
import MapControls from './map/MapControls';
import EventInfoWindow from './map/EventInfoWindow';
import {
  defaultCenter,
  defaultZoom,
  minZoom,
  maxZoom,
  polandBounds,
} from './map/constants';
import { getTileLayerConfig } from './map/leafletTiles';
import { safeInvalidateMapSize } from './map/leafletMapUtils';
import { useMapMarkers, useMapSearch, useUserLocation, useMapStyle } from './map/hooks';
import './Map.css';
import './map/MapLeafletZoom.css';

function postDebugLog(payload) {
  fetch('http://127.0.0.1:7543/ingest/6827e7d0-9c41-4ca4-995b-041f97954400', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': 'df8df3',
    },
    body: JSON.stringify({
      sessionId: 'df8df3',
      timestamp: Date.now(),
      ...payload,
    }),
  }).catch(() => {});
}

/** Pinezki tylko z latitude/longitude zapisanych w bazie (bez geokodowania w przeglądarce). */
function mapEventsWithDbCoordinates(list) {
  if (!list || !list.length) return [];
  return list
    .map((event) => {
      const lat = event.latitude != null ? Number(event.latitude) : null;
      const lng = event.longitude != null ? Number(event.longitude) : null;
      if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
        return null;
      }
      return { ...event, coordinates: { lat, lng } };
    })
    .filter(Boolean);
}

const SELECTED_EVENT_PANEL_EXIT_MS = 240;

function Map({
  openSearch = false,
  onSearchOpened = null,
  externalEvents = null,
  externalLoading = false,
  onSwitchToListPanel = null,
  drawerBackdropOpen = false,
  onDrawerBackdropClose = null,
  /** Kategoria z panelu Odkrywaj (np. `sport`); `all` = bez chipa */
  eventCategory = 'all',
  /** Usunięcie chipa kategorii — np. `() => setActiveCategory('all')` */
  onDismissEventCategory = null,
  /** Gdy ustawione, Fullscreen API obejmuje ten element (np. mapa + szuflada); inaczej tylko kontener mapy. */
  fullscreenHostRef = null,
}) {
  const { t } = useTranslation(['events', 'discover']);
  const { openPopup } = usePopup();

  const [events, setEvents] = useState([]);
  const [eventsWithCoords, setEventsWithCoords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [renderedSelectedEvent, setRenderedSelectedEvent] = useState(null);
  const [selectedEventPanelLeaving, setSelectedEventPanelLeaving] = useState(false);
  const [visibleEvents, setVisibleEvents] = useState([]);
  const [hiddenMarkersCount, setHiddenMarkersCount] = useState(0);
  const [emptyHintDismissed, setEmptyHintDismissed] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapInitError, setMapInitError] = useState(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [mapFiltersOpen, setMapFiltersOpen] = useState(false);
  const [mapDiscoverFilters, setMapDiscoverFilters] = useState(() => defaultMapDiscoverFilters());
  const [friendIdSet, setFriendIdSet] = useState(() => new Set());
  const [currentUserId, setCurrentUserId] = useState(null);
  const mapStyle = useMapStyle();

  const useExternalEvents = externalEvents !== null;
  const mapRef = useRef(null);
  /** Aktualny filtr okręgu wyszukiwania — ref, żeby useMapMarkers mógł czytać przy każdym przeliczeniu markerów */
  const searchRadiusFilterRef = useRef(null);
  /** Kontener mapy (Fullscreen API) — cały UI mapy zostaje w pełnym ekranie. */
  const fullscreenContainerRef = useRef(null);
  /** Stały element - Leaflet przejmuje go przy inicjalizacji (nie wolno odmontowywać). */
  const mapPaneRef = useRef(null);
  const tileLayerRef = useRef(null);
  const saveMapStateTimeoutRef = useRef(null);
  const reloadEventsTimeoutRef = useRef(null);
  const selectedEventPanelTimeoutRef = useRef(null);
  const prevDrawerBackdropOpenRef = useRef(drawerBackdropOpen);
  const mapMoveEndLastTsRef = useRef(0);
  /** Ostatni zoom po prawdziwej zmianie skali — Leaflet `flyTo` kończy się zawsze `zoomend`, nawet gdy poziom zoomu się nie zmienił. */
  const lastZoomForMarkerSnapFxRef = useRef(null);

  const handleMarkerClick = useCallback((event) => {
    setSelectedEvent(event);
    const map = mapRef.current;
    if (map && event.coordinates) {
      const target = L.latLng(event.coordinates.lat, event.coordinates.lng);
      const currentZoom = map.getZoom();
      const targetZoom = currentZoom < 16 ? 16 : currentZoom;
      map.flyTo(target, targetZoom, { animate: true, duration: 0.35 });
    }
  }, []);

  useEffect(() => {
    if (selectedEventPanelTimeoutRef.current) {
      clearTimeout(selectedEventPanelTimeoutRef.current);
      selectedEventPanelTimeoutRef.current = null;
    }

    if (selectedEvent && selectedEvent.coordinates) {
      if (
        renderedSelectedEvent &&
        renderedSelectedEvent.id !== selectedEvent.id
      ) {
        setSelectedEventPanelLeaving(true);
        selectedEventPanelTimeoutRef.current = setTimeout(() => {
          setRenderedSelectedEvent(selectedEvent);
          setSelectedEventPanelLeaving(false);
          selectedEventPanelTimeoutRef.current = null;
        }, SELECTED_EVENT_PANEL_EXIT_MS);
        return undefined;
      }

      setRenderedSelectedEvent(selectedEvent);
      setSelectedEventPanelLeaving(false);
      return undefined;
    }

    if (renderedSelectedEvent) {
      setSelectedEventPanelLeaving(true);
      selectedEventPanelTimeoutRef.current = setTimeout(() => {
        setRenderedSelectedEvent(null);
        setSelectedEventPanelLeaving(false);
        selectedEventPanelTimeoutRef.current = null;
      }, SELECTED_EVENT_PANEL_EXIT_MS);
    }

    return () => {
      if (selectedEventPanelTimeoutRef.current) {
        clearTimeout(selectedEventPanelTimeoutRef.current);
        selectedEventPanelTimeoutRef.current = null;
      }
    };
  }, [selectedEvent, renderedSelectedEvent]);

  useEffect(() => {
    if (drawerBackdropOpen && !prevDrawerBackdropOpenRef.current) {
      setSelectedEvent(null);
    }
    prevDrawerBackdropOpenRef.current = drawerBackdropOpen;
  }, [drawerBackdropOpen]);

  const {
    createMarkers,
    updateVisibleMarkers,
    clearMarkers,
    highlightMarker,
    unhighlightMarker,
    playZoomMarkersFx,
  } = useMapMarkers(
    mapRef,
    handleMarkerClick,
    searchRadiusFilterRef
  );

  useEffect(() => {
    if (selectedEvent) {
      highlightMarker(selectedEvent.id);
    } else {
      unhighlightMarker();
    }
  }, [selectedEvent, highlightMarker, unhighlightMarker]);

  /** Przy realnej zmianie zoomu: krótki „snap” pinezek. `zoomend` po `flyTo` odpala się także przy tym samym zoomie — wtedy pomijamy. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return undefined;
    lastZoomForMarkerSnapFxRef.current = map.getZoom();
    let debounceId = 0;
    const onZoomEnd = () => {
      const z = map.getZoom();
      if (lastZoomForMarkerSnapFxRef.current === z) {
        return;
      }
      lastZoomForMarkerSnapFxRef.current = z;
      window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        debounceId = 0;
        playZoomMarkersFx();
      }, 110);
    };
    map.on('zoomend', onZoomEnd);
    return () => {
      map.off('zoomend', onZoomEnd);
      window.clearTimeout(debounceId);
    };
  }, [mapReady, playZoomMarkersFx]);

  const { userLocation, getUserLocation, centerOnUserLocation } = useUserLocation(mapRef, mapReady);

  const userLocationPresentRef = useRef(false);
  userLocationPresentRef.current = userLocation != null;

  /** Widok mapy sprzed ostatniego „Zlokalizuj mnie” — drugie kliknięcie przywraca. */
  const locateUndoRef = useRef(null);
  const [locateUndoAvailable, setLocateUndoAvailable] = useState(false);

  const handleLocateMe = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    if (locateUndoRef.current) {
      const { lat, lng, zoom } = locateUndoRef.current;
      map.setView(L.latLng(lat, lng), zoom, { animate: true });
      locateUndoRef.current = null;
      setLocateUndoAvailable(false);
      return;
    }

    const c = map.getCenter();
    const z = map.getZoom();
    locateUndoRef.current = { lat: c.lat, lng: c.lng, zoom: z };
    setLocateUndoAvailable(true);

    if (userLocationPresentRef.current) {
      centerOnUserLocation();
    } else {
      getUserLocation();
    }
  }, [centerOnUserLocation, getUserLocation]);

  const getFullscreenElement = useCallback(() => {
    return fullscreenHostRef?.current ?? fullscreenContainerRef.current;
  }, [fullscreenHostRef]);

  const syncMapFullscreenState = useCallback(() => {
    const el = getFullscreenElement();
    const fs = document.fullscreenElement || document.webkitFullscreenElement;
    setIsMapFullscreen(!!el && fs === el);
  }, [getFullscreenElement]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', syncMapFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncMapFullscreenState);
    return () => {
      document.removeEventListener('fullscreenchange', syncMapFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncMapFullscreenState);
    };
  }, [syncMapFullscreenState]);

  const invalidateMapSize = useCallback(() => {
    requestAnimationFrame(() => {
      safeInvalidateMapSize(mapRef.current, { animate: false, pan: false });
    });
  }, []);

  useEffect(() => {
    if (mapReady) {
      invalidateMapSize();
    }
  }, [isMapFullscreen, mapReady, invalidateMapSize]);

  const handleToggleMapFullscreen = useCallback(() => {
    const el = getFullscreenElement();
    if (!el) {
      return;
    }
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl === el) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
      return;
    }
    if (typeof el.requestFullscreen === 'function') {
      el
        .requestFullscreen()
        .then(() => {
          invalidateMapSize();
        })
        .catch(() => {});
    } else if (typeof el.webkitRequestFullscreen === 'function') {
      try {
        el.webkitRequestFullscreen();
        invalidateMapSize();
      } catch {
        /* ignore */
      }
    }
  }, [getFullscreenElement, invalidateMapSize]);

  /** Pełny ekran ukrywa modale spoza drzewa DOM — przed listą wychodzimy z fullscreen (ten sam cel co przycisk „Pełny ekran”). */
  const leaveMapFullscreenLayer = useCallback(async () => {
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (!fsEl) return;
    const target = getFullscreenElement();
    if (!target || fsEl !== target) return;
    try {
      if (typeof document.exitFullscreen === 'function') {
        await document.exitFullscreen();
      } else if (typeof document.webkitExitFullscreen === 'function') {
        document.webkitExitFullscreen();
      }
    } catch {
      /* ignore */
    }
  }, [getFullscreenElement]);

  const handleShowMapEventList = useCallback(() => {
    void (async () => {
      await leaveMapFullscreenLayer();
      requestAnimationFrame(() => {
        if (typeof onSwitchToListPanel === 'function') {
          onSwitchToListPanel();
          return;
        }
        openPopup('discover-map-list', { events: visibleEvents }, 0);
      });
    })();
  }, [leaveMapFullscreenLayer, onSwitchToListPanel, openPopup, visibleEvents]);

  const saveMapState = useCallback((center, zoom, visibleEventsList) => {
    try {
      const mapState = {
        center: { lat: center.lat, lng: center.lng },
        zoom,
        visibleEvents: visibleEventsList,
        timestamp: Date.now(),
      };
      localStorage.setItem('mapState', JSON.stringify(mapState));
      localStorage.setItem('mapVisibleEvents', JSON.stringify(visibleEventsList));
    } catch (err) {
      console.warn('Failed to save map state to localStorage:', err);
    }
  }, []);

  const updateVisibleMarkersCount = useCallback((trigger = 'unknown') => {
    const perfStart = performance.now();
    const map = mapRef.current;
    if (!map) {
      setVisibleEvents([]);
      setHiddenMarkersCount(0);
      setEmptyHintDismissed(false);
      return;
    }

    const center = map.getCenter();
    const zoom = map.getZoom();
    const { hiddenCount = 0, visibleEvents: visibleEventsList = [] } = updateVisibleMarkers();

    setHiddenMarkersCount(hiddenCount);
    setVisibleEvents(visibleEventsList);

    if (visibleEventsList.length > 0) {
      setEmptyHintDismissed(false);
    } else {
      setEmptyHintDismissed(false);
    }

    if (saveMapStateTimeoutRef.current) {
      clearTimeout(saveMapStateTimeoutRef.current);
    }
    saveMapStateTimeoutRef.current = setTimeout(() => {
      if (center && zoom !== undefined) {
        saveMapState(center, zoom, visibleEventsList);
      }
    }, 500);

    const elapsedMs = Math.round((performance.now() - perfStart) * 100) / 100;
    // #region agent log
    postDebugLog({
      runId: 'baseline',
      hypothesisId: 'H3',
      location: 'Map.js:updateVisibleMarkersCount',
      message: 'Map visible marker state update completed',
      data: {
        trigger,
        visibleEventsCount: visibleEventsList.length,
        hiddenCount,
        zoom,
        elapsedMs,
      },
    });
    // #endregion
  }, [saveMapState, updateVisibleMarkers]);

  const handleSearchComplete = useCallback(() => {
    updateVisibleMarkersCount();
  }, [updateVisibleMarkersCount]);

  const {
    searchAddress,
    searchRange,
    searchSuggestions,
    searching,
    searchOpen,
    searchCenter,
    setSearchOpen,
    setSearchRange,
    handleAddressInput,
    handleSearch,
    handleSuggestionClick,
    closeSearch,
    clearSearchLocation,
    cleanup: cleanupSearch,
  } = useMapSearch(mapRef, handleSearchComplete);

  const radiusFilter = useMemo(() => {
    if (!searchCenter) return null;
    const lat = Number(searchCenter.lat);
    const lng = Number(searchCenter.lng);
    const r = Number(searchRange);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(r) || r <= 0) return null;
    return { lat, lng, radiusM: r * 1000 };
  }, [searchCenter, searchRange]);

  searchRadiusFilterRef.current = radiusFilter;

  const mapActiveFilterChips = useMemo(
    () =>
      buildMapActiveFilterChips(
        {
          searchCenter,
          searchAddress,
          searchRange,
          mapDiscoverFilters,
          eventCategory,
        },
        t
      ),
    [searchCenter, searchAddress, searchRange, mapDiscoverFilters, eventCategory, t]
  );

  const handleDismissActiveFilter = useCallback(
    (chipId) => {
      if (chipId === 'search-area') {
        clearSearchLocation();
        return;
      }
      if (chipId === 'eventCategory') {
        if (typeof onDismissEventCategory === 'function') {
          onDismissEventCategory();
        }
        return;
      }
      setMapDiscoverFilters((prev) => {
        const next = { ...prev };
        switch (chipId) {
          case 'datePreset':
            next.datePreset = '';
            break;
          case 'dateFrom':
            next.dateFrom = '';
            break;
          case 'dateTo':
            next.dateTo = '';
            break;
          case 'price':
            next.priceMin = '';
            next.priceMax = '';
            break;
          case 'pricing':
            next.isFree = null;
            break;
          case 'participantsMin':
            next.minParticipants = '';
            break;
          case 'participantsMax':
            next.maxParticipants = '';
            break;
          case 'friends':
            next.friendsOnly = false;
            break;
          case 'mine':
            next.mineOnly = false;
            break;
          default:
            break;
        }
        if (next.datePreset === 'custom' && !next.dateFrom && !next.dateTo) {
          next.datePreset = '';
        }
        if (!currentUserId) next.mineOnly = false;
        if (!currentUserId || friendIdSet.size === 0) next.friendsOnly = false;
        return next;
      });
    },
    [clearSearchLocation, currentUserId, friendIdSet.size, onDismissEventCategory]
  );

  const eventsInSearchRadiusCount = useMemo(() => {
    if (!searchCenter) return null;
    const lat = Number(searchCenter.lat);
    const lng = Number(searchCenter.lng);
    const rKm = Number(searchRange);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(rKm) || rKm <= 0) return null;
    const c = L.latLng(lat, lng);
    const rM = rKm * 1000;
    let n = 0;
    for (const e of eventsWithCoords) {
      if (!e?.coordinates) continue;
      if (c.distanceTo(L.latLng(e.coordinates.lat, e.coordinates.lng)) <= rM) n += 1;
    }
    return n;
  }, [searchCenter, searchRange, eventsWithCoords]);

  useEffect(() => {
    if (!mapReady) return;
    updateVisibleMarkersCount();
  }, [searchCenter, searchRange, mapReady, updateVisibleMarkersCount]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      setCurrentUserId(user?.id ?? null);
      if (!user?.id) {
        setFriendIdSet(new Set());
        return;
      }
      const fr = await getFriends(user.id);
      if (cancelled) return;
      if (fr.success && Array.isArray(fr.data)) {
        setFriendIdSet(new Set(fr.data.map((f) => f.id)));
      } else {
        setFriendIdSet(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rawEventList = useMemo(() => {
    const list = useExternalEvents ? externalEvents || [] : events;
    return list.filter((e) => isEventNotFinishedYet(e));
  }, [useExternalEvents, externalEvents, events]);

  const filteredEventList = useMemo(
    () =>
      applyDiscoverEventFilters(rawEventList, mapDiscoverFilters, {
        friendIds: friendIdSet,
        currentUserId,
      }),
    [rawEventList, mapDiscoverFilters, friendIdSet, currentUserId]
  );

  useEffect(() => {
    setEventsWithCoords(mapEventsWithDbCoordinates(filteredEventList));
  }, [filteredEventList]);

  const combinedFilteredTotalEvents = useMemo(() => {
    if (searchCenter) return eventsInSearchRadiusCount;
    if (mapDiscoverFiltersActive(mapDiscoverFilters)) return filteredEventList.length;
    return null;
  }, [searchCenter, eventsInSearchRadiusCount, mapDiscoverFilters, filteredEventList.length]);

  const handleMapFiltersApply = useCallback(
    (draft) => {
      const next = { ...draft };
      if (!currentUserId) next.mineOnly = false;
      if (!currentUserId || friendIdSet.size === 0) next.friendsOnly = false;
      setMapDiscoverFilters(next);
    },
    [currentUserId, friendIdSet.size]
  );

  const handleMapFiltersReset = useCallback((cleared) => {
    setMapDiscoverFilters(cleared);
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const todayString = formatLocalYmd(new Date());

      let query = supabase
        .from('events')
        .select('*')
        .gte('event_date', todayString)
        .order('event_date', { ascending: true })
        .order('start_time', { ascending: true });

      try {
        query = query.eq('is_cancelled', false);
      } catch (err) {
        console.warn('is_cancelled column not found, loading all events');
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        if (fetchError.message && fetchError.message.includes('is_cancelled')) {
          const { data: retryData, error: retryError } = await supabase
            .from('events')
            .select('*')
            .gte('event_date', todayString)
            .order('event_date', { ascending: true })
            .order('start_time', { ascending: true });

          if (retryError) throw retryError;
          setEvents(retryData || []);
          return;
        }
        throw fetchError;
      }

      const filteredData = (data || []).filter((event) => event.is_cancelled !== true);
      setEvents(filteredData);
    } catch (err) {
      console.error('Error loading events:', err);
      setError(err.message || t('discover.list.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleViewDetails = (eventId) => {
    setSelectedEvent(null);
    openPopup('event-details', { eventId }, 0);
  };

  const handleViewProfile = (userId) => {
    setSelectedEvent(null);
    openPopup('profile', { userId }, 0);
  };

  useEffect(() => {
    if (useExternalEvents) {
      setEvents(externalEvents || []);
      setLoading(externalLoading);
      setEmptyHintDismissed(false);

      if (!externalEvents?.length) {
        setVisibleEvents([]);
        setHiddenMarkersCount(0);
        clearMarkers();
      }
    }
  }, [useExternalEvents, externalEvents, externalLoading, clearMarkers]);

  useEffect(() => {
    if (mapReady && !useExternalEvents) {
      loadEvents();
      getUserLocation();
    }
  }, [mapReady, loadEvents, getUserLocation, useExternalEvents]);

  useEffect(() => {
    if (!mapReady || useExternalEvents) {
      return undefined;
    }

    const channelName = `discover-map-events-${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        () => {
          if (reloadEventsTimeoutRef.current) {
            clearTimeout(reloadEventsTimeoutRef.current);
          }
          reloadEventsTimeoutRef.current = setTimeout(() => {
            loadEvents();
          }, 350);
        }
      )
      .subscribe();

    return () => {
      if (reloadEventsTimeoutRef.current) {
        clearTimeout(reloadEventsTimeoutRef.current);
        reloadEventsTimeoutRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [mapReady, useExternalEvents, loadEvents]);

  useEffect(() => {
    if (openSearch && mapReady) {
      setTimeout(() => {
        setSearchOpen(true);
        if (onSearchOpened) {
          onSearchOpened();
        }
      }, 300);
    }
  }, [openSearch, mapReady, onSearchOpened, setSearchOpen]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && mapReady) {
      // #region agent log
      postDebugLog({
        runId: 'baseline',
        hypothesisId: 'H2',
        location: 'Map.js:markersEffect',
        message: 'eventsWithCoords effect triggered marker rebuild',
        data: {
          eventsWithCoordsCount: eventsWithCoords.length,
          mapReady,
        },
      });
      // #endregion
      createMarkers(eventsWithCoords);
      updateVisibleMarkersCount('post-create');
      setTimeout(() => {
        updateVisibleMarkersCount('post-create-timeout');
      }, 100);
    }
  }, [eventsWithCoords, mapReady, createMarkers, updateVisibleMarkersCount, clearMarkers, cleanupSearch]);

  useEffect(() => {
    return () => {
      clearMarkers();
      cleanupSearch();
    };
  }, [clearMarkers, cleanupSearch]);

  useEffect(() => {
    const el = mapPaneRef.current;
    if (!el || mapRef.current) {
      return undefined;
    }

    let cancelled = false;
    try {
      const southWest = L.latLng(polandBounds.south, polandBounds.west);
      const northEast = L.latLng(polandBounds.north, polandBounds.east);
      const maxBounds = L.latLngBounds(southWest, northEast);

      const map = L.map(el, {
        center: [defaultCenter.lat, defaultCenter.lng],
        zoom: defaultZoom,
        minZoom,
        maxZoom,
        maxBounds,
        maxBoundsViscosity: 0.65,
        scrollWheelZoom: true,
        zoomControl: true,
      });

      const tiles = getTileLayerConfig(mapStyle);
      tileLayerRef.current = L.tileLayer(tiles.url, {
        attribution: tiles.attribution,
        // maxNativeZoom zapobiega "znikaniu" kafelków przy dużym zbliżeniu:
        // po przekroczeniu natywnego zoomu kafelki są bezpiecznie skalowane.
        maxNativeZoom: tiles.maxNativeZoom ?? 19,
        maxZoom,
      }).addTo(map);

      mapRef.current = map;

      try {
        const savedMapState = localStorage.getItem('mapState');
        if (savedMapState) {
          const state = JSON.parse(savedMapState);
          if (state.center && state.zoom) {
            map.setView([state.center.lat, state.center.lng], state.zoom);
          }
        }
      } catch (err) {
        console.warn('Failed to restore map state:', err);
      }

      map.on('click', () => setSelectedEvent(null));
      map.on('moveend', () => {
        const now = Date.now();
        const deltaMs = mapMoveEndLastTsRef.current ? now - mapMoveEndLastTsRef.current : null;
        mapMoveEndLastTsRef.current = now;
        // #region agent log
        postDebugLog({
          runId: 'baseline',
          hypothesisId: 'H3',
          location: 'Map.js:moveend',
          message: 'Leaflet moveend fired',
          data: {
            deltaFromPreviousMoveendMs: deltaMs,
          },
        });
        // #endregion
        updateVisibleMarkersCount('moveend');
      });

      setTimeout(() => {
        updateVisibleMarkersCount('post-init-timeout');
      }, 100);

      if (!cancelled) {
        setMapReady(true);
      }
    } catch (e) {
      if (!cancelled) {
        setMapInitError(e?.message || 'Map init failed');
      }
    }

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      tileLayerRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init once on mount
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !tileLayerRef.current) return;
    const tiles = getTileLayerConfig(mapStyle);
    tileLayerRef.current.setUrl(tiles.url);
    tileLayerRef.current.options.attribution = tiles.attribution;
    tileLayerRef.current.options.maxNativeZoom = tiles.maxNativeZoom ?? 19;
    tileLayerRef.current.options.maxZoom = maxZoom;
    if (typeof map.attributionControl?.setPrefix === 'function') {
      map.attributionControl.setPrefix(false);
    }
  }, [mapStyle, mapReady]);

  /** Płynne kurczenie kolumny (szuflada Odkryj): Leaflet przelicza rozmiar przy każdej klatce zmiany layoutu */
  useEffect(() => {
    const el = mapPaneRef.current;
    if (!el || !mapReady) {
      return undefined;
    }

    let rafId = 0;
    const scheduleInvalidate = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        safeInvalidateMapSize(mapRef.current, { animate: false, pan: false });
      });
    };

    const ro = new ResizeObserver(scheduleInvalidate);
    ro.observe(el);
    scheduleInvalidate();

    return () => {
      ro.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [mapReady]);

  const handleSearchClose = useCallback(
    (isOpen) => {
      if (!isOpen) {
        closeSearch();
      } else {
        setMapFiltersOpen(false);
        setSearchOpen(true);
      }
    },
    [closeSearch, setSearchOpen]
  );

  const handleMapFiltersToggleOpen = useCallback(
    (open) => {
      setMapFiltersOpen(open);
      if (open) {
        setSearchOpen(false);
      }
    },
    [setSearchOpen]
  );

  return (
    <div
      ref={fullscreenContainerRef}
      className={`map-container map-container--leaflet map-palette map-palette--${mapStyle}`}
    >
      <div ref={mapPaneRef} className="map-leaflet-root" aria-hidden={!mapReady} />
      {drawerBackdropOpen && typeof onDrawerBackdropClose === 'function' && (
        <DiscoverDrawerMapBackdrop onClose={onDrawerBackdropClose} />
      )}
      {mapInitError && (
        <div className="map-error-overlay">
          <div className="map-error">
            <p>{t('discover.list.error')}</p>
            <p style={{ fontSize: '0.9rem', color: '#666' }}>{mapInitError}</p>
          </div>
        </div>
      )}
      {!mapReady && !mapInitError && (
        <div className="map-loading-overlay">
          <p>{t('discover.list.loading')}</p>
        </div>
      )}
      {error && (
        <div className="map-error-overlay">
          <div className="map-error">
            <p>{error}</p>
            <button type="button" onClick={loadEvents} className="retry-button">
              {t('discover.list.retry')}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="map-loading-overlay">
          <p>{t('discover.list.loading')}</p>
        </div>
      )}

      {renderedSelectedEvent && renderedSelectedEvent.coordinates && (
        <div
          className={`map-selected-event-panel ${selectedEventPanelLeaving ? 'map-selected-event-panel--leaving' : ''}`}
          key={renderedSelectedEvent.id}
        >
          <EventInfoWindow
            event={renderedSelectedEvent}
            onClose={() => setSelectedEvent(null)}
            onViewDetails={handleViewDetails}
            onViewProfile={handleViewProfile}
            userLocation={userLocation}
          />
        </div>
      )}

      <SearchPanel
        searchOpen={searchOpen}
        onClose={handleSearchClose}
        searchAddress={searchAddress}
        onAddressChange={handleAddressInput}
        onSearch={handleSearch}
        searchSuggestions={searchSuggestions}
        onSuggestionClick={handleSuggestionClick}
        searchRange={searchRange}
        onRangeChange={setSearchRange}
        searching={searching}
        searchLocationActive={Boolean(searchCenter)}
      />

      <MapFiltersPanel
        filtersOpen={mapFiltersOpen}
        onToggleOpen={handleMapFiltersToggleOpen}
        filters={mapDiscoverFilters}
        activeFilterCount={mapActiveFilterChips.length}
        onApply={handleMapFiltersApply}
        onReset={handleMapFiltersReset}
        friendsFilterDisabled={!currentUserId || friendIdSet.size === 0}
        mineFilterDisabled={!currentUserId}
      />

      {!loading && !error && (
        <LimitWarning hiddenCount={hiddenMarkersCount} onClose={() => setHiddenMarkersCount(0)} />
      )}

      <MapControls
        onLocateMe={handleLocateMe}
        locateUndoAvailable={locateUndoAvailable}
        mapFullscreen={isMapFullscreen}
        onToggleMapFullscreen={handleToggleMapFullscreen}
        visibleEvents={visibleEvents}
        activeFilterChips={mapActiveFilterChips}
        onDismissActiveFilter={handleDismissActiveFilter}
        onShowList={handleShowMapEventList}
        loading={loading}
        error={error}
        totalEvents={events.length}
        filteredTotalEvents={combinedFilteredTotalEvents}
        emptyHintDismissed={emptyHintDismissed}
        onDismissEmptyHint={() => setEmptyHintDismissed(true)}
      />
    </div>
  );
}

export default Map;

